import React, { forwardRef, Fragment, useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Stack,
  TextField,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';
import ReactSelect, { MultiValue, SingleValue, components } from 'react-select';
import useJurisdictions from 'data_sources/useJurisdictions';
import { Jurisdiction } from 'types/jurisdiction';

interface JurisdictionOption {
  value: string;
  label: string;
  jurisdiction: Jurisdiction;
}

export type JurisdictionPickerProps = {
  allowBlank?: boolean;
  allowMultiple?: boolean;
  id: string;
  label?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string | undefined;
  size?: string;
  sx?: SxProps<Theme>;
  filterIds?: string[];
  prefix?: string;
};

const JurisdictionPicker = forwardRef(
  (
    {
      allowBlank = false,
      allowMultiple = false,
      id,
      label,
      value = [],
      onChange,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
      sx,
      filterIds,
      prefix,
    }: JurisdictionPickerProps,
    ref
  ) => {
    const jurisdictionsQuery = useJurisdictions();

    const SingleValue = ({ children, ...props }: any) => (
      <components.SingleValue {...props}>
        {props.data?.value
          ? `${prefix ? prefix + ': ' : ''}${children}`
          : (placeholder ?? '-- Select a jurisdiction --')}
      </components.SingleValue>
    );

    const jurisdictionOptions = useMemo(() => {
      const filteredJurisdictions = filterIds
        ? (jurisdictionsQuery.data ?? []).filter((j) => filterIds.includes(j.id))
        : (jurisdictionsQuery.data ?? []);

      return [
        ...(allowBlank ? [{ value: '', label: placeholder ?? '-- Select a jurisdiction --' }] : []),
        ...filteredJurisdictions.map((x) => {
          return { value: x.id, label: `${x.name ?? ''} (${x.abbreviation})`, jurisdiction: x };
        }),
      ];
    }, [jurisdictionsQuery.data, allowBlank, filterIds]);

    const selectedOptions = useMemo(() => {
      return jurisdictionOptions.filter((x) => (value ?? []).includes(x.value));
    }, [value, jurisdictionOptions]);

    return jurisdictionsQuery.isLoading ? (
      <CircularProgress />
    ) : (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={sx}
      >
        {!!label && <InputLabel required={required}>{label}</InputLabel>}
        <ReactSelect
          name={id}
          value={selectedOptions}
          options={jurisdictionOptions}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          components={{ SingleValue }}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isMulti={allowMultiple}
          isSearchable
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              const newVals = newValue as MultiValue<JurisdictionOption>;
              onChange((newValue ?? []).map((x) => x.value));
            } else {
              const newVal = newValue as SingleValue<JurisdictionOption>;
              onChange(!!newVal ? [newVal.value] : []);
            }
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default JurisdictionPicker;
